import {
  ConditionComparator,
  FieldTypeKind,
  FormFieldCondition,
  FormFieldConditionForm,
  FormFieldConditionJourney,
  FormFieldValue,
} from '@components/form/v2';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
} from '@components/text-editors/addons/field-mapping';

export type ConditionDraftForm = {
  source: 'form';
  id: string;
  questionId?: string;
  comparator?: ConditionComparator;
  value?: FormFieldValue;
};

export type ConditionDraftJourney = {
  source: 'journey';
  id: string;
  variable?: FieldMappingFieldItem;
  comparator?: ConditionComparator;
  value?: FormFieldValue;
};

export type ConditionDraft = ConditionDraftForm | ConditionDraftJourney;

const kindToFieldType: Partial<Record<FieldTypeKind, FieldType>> = {
  'input-dropdown': 'select',
  'input-single-choice': 'select',
  'input-multiple-choice': 'multi_select',
  'input-date': 'date',
  'input-number': 'number',
  'input-file': 'file',
  'input-text-small': 'text',
  'input-text-large': 'text',
  'input-email': 'text',
  'input-phone': 'text',
  'input-kennitala': 'text',
};

export const getElementTypeForCondition = (
  condition: ConditionDraft,
  getFieldKind: (id: string) => FieldTypeKind | undefined
): FieldType | undefined => {
  if (condition.source === 'journey') {
    return condition.variable?.type;
  }
  if (!condition.questionId) return undefined;
  const kind = getFieldKind(condition.questionId);
  if (!kind) return undefined;
  return kindToFieldType[kind];
};

export const resetConditionForNewElement = (
  condition: ConditionDraft,
  next: { questionId: string } | { variable: FieldMappingFieldItem }
): ConditionDraft => {
  if ('variable' in next) {
    return { source: 'journey', id: condition.id, variable: next.variable };
  }
  return { source: 'form', id: condition.id, questionId: next.questionId };
};

export const MULTI_COMPARATORS: ReadonlySet<ConditionComparator> = new Set(['any_of', 'none_of']);

export const isMultiComparator = (comparator: ConditionComparator): boolean =>
  MULTI_COMPARATORS.has(comparator);

const VALUELESS_COMPARATORS = new Set<string>(['empty', 'nempty']);

const comparatorRequiresValue = (comparator: ConditionComparator): boolean =>
  !VALUELESS_COMPARATORS.has(comparator);

const hasValue = (value: FormFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const hasElement = (condition: ConditionDraft): boolean => {
  if (condition.source === 'journey') return Boolean(condition.variable);
  return Boolean(condition.questionId);
};

export const canSaveCondition = (condition: ConditionDraft): boolean => {
  if (!hasElement(condition)) return false;
  if (!condition.comparator) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

const reshapeValueForComparator = (
  value: FormFieldValue | undefined,
  willBeMulti: boolean
): FormFieldValue => {
  if (willBeMulti) {
    if (Array.isArray(value)) return value;
    return hasValue(value) ? [value as string | number] : [];
  }
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
};

export const applyComparatorChange = <T extends FormFieldCondition>(
  condition: T,
  next: ConditionComparator
): T => {
  if (next === condition.comparator) return condition;
  const wasMulti = isMultiComparator(condition.comparator);
  const willBeMulti = isMultiComparator(next);
  if (wasMulti === willBeMulti) {
    return { ...condition, comparator: next };
  }
  return {
    ...condition,
    comparator: next,
    value: reshapeValueForComparator(condition.value, willBeMulti),
  };
};

const toggleMultiValue = (
  current: FormFieldValue | undefined,
  candidate: string
): Array<string | number> => {
  const arr = Array.isArray(current) ? current : [];
  return arr.includes(candidate) ? arr.filter((entry) => entry !== candidate) : [...arr, candidate];
};

export const applyValueChange = <T extends FormFieldCondition>(
  condition: T,
  next: FormFieldValue
): T => {
  if (!isMultiComparator(condition.comparator) || typeof next !== 'string') {
    return { ...condition, value: next };
  }
  return { ...condition, value: toggleMultiValue(condition.value, next) };
};

export const isCompleteCondition = (condition: FormFieldCondition): boolean => {
  if (condition.source === 'form' && !condition.questionId) return false;
  if (condition.source === 'journey' && !condition.variable) return false;
  if (!condition.comparator) return false;
  const valueIsArray = Array.isArray(condition.value);
  const expectArray = isMultiComparator(condition.comparator);
  if (expectArray !== valueIsArray) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

export const getRenderableConditionCount = (conditions: FormFieldCondition[]): number =>
  conditions.length;

export const journeyVariableGroupsAreNonEmpty = (
  groups: ReadonlyArray<FieldMappingGroup>
): boolean => groups.some((group) => group.fields.length > 0);

const TEXT_LIKE_COMPARATORS: ReadonlyArray<ConditionComparator> = [
  'is',
  'not_is',
  'starts',
  'not_starts',
  'contains',
  'not_contains',
];

const FORM_COMPARATORS_BY_FIELD_TYPE: Partial<
  Record<FieldType, ReadonlyArray<ConditionComparator>>
> = {
  select: ['is', 'not_is', 'any_of', 'none_of'],
  multi_select: ['any_of', 'none_of'],
  options: ['is', 'not_is', 'any_of', 'none_of'],
  date: ['before', 'on', 'after'],
  text: TEXT_LIKE_COMPARATORS,
  email: TEXT_LIKE_COMPARATORS,
  phone: TEXT_LIKE_COMPARATORS,
  paragraph: TEXT_LIKE_COMPARATORS,
  number: ['is', 'not_is'],
  decimal: ['is', 'not_is'],
  boolean: ['is', 'not_is'],
  kennitala: TEXT_LIKE_COMPARATORS,
};

const TEXT_LIKE_FIELD_TYPES: ReadonlyArray<FieldType> = (
  Object.keys(FORM_COMPARATORS_BY_FIELD_TYPE) as FieldType[]
).filter((fieldType) => FORM_COMPARATORS_BY_FIELD_TYPE[fieldType] === TEXT_LIKE_COMPARATORS);

export const isInlineTextLikeFieldType = (fieldType: FieldType | undefined): boolean =>
  fieldType !== undefined && TEXT_LIKE_FIELD_TYPES.includes(fieldType);

export const getComparatorsForFieldType = (
  fieldType: FieldType | undefined
): ReadonlyArray<ConditionComparator> => {
  if (!fieldType) return [];
  return FORM_COMPARATORS_BY_FIELD_TYPE[fieldType] ?? [];
};

export const buildResetFormCondition = (
  conditionId: string,
  newQuestionId: string,
  availableComparators: ReadonlyArray<ConditionComparator>
): FormFieldConditionForm => {
  const comparator = availableComparators[0] ?? 'is';
  return {
    source: 'form',
    id: conditionId,
    questionId: newQuestionId,
    comparator,
    value: isMultiComparator(comparator) ? [] : '',
  };
};

export const buildResetJourneyCondition = (
  conditionId: string,
  variable: FieldMappingFieldItem,
  availableComparators: ReadonlyArray<ConditionComparator>
): FormFieldConditionJourney => {
  const comparator = availableComparators[0] ?? 'is';
  return {
    source: 'journey',
    id: conditionId,
    variable,
    comparator,
    value: isMultiComparator(comparator) ? [] : '',
  };
};

export type JourneyDraftMode = 'add' | { kind: 'edit'; conditionId: string };

export type JourneyConditionDraft = {
  mode: JourneyDraftMode;
  variable: FieldMappingFieldItem;
  comparator: ConditionComparator | undefined;
  value: FormFieldValue | undefined;
};

export const createJourneyDraft = (params: {
  mode: JourneyDraftMode;
  variable: FieldMappingFieldItem;
}): JourneyConditionDraft => ({
  mode: params.mode,
  variable: params.variable,
  comparator: undefined,
  value: undefined,
});

export const applyJourneyDraftComparator = (
  draft: JourneyConditionDraft,
  comparator: ConditionComparator
): JourneyConditionDraft => {
  const wasMulti = draft.comparator ? isMultiComparator(draft.comparator) : false;
  const willBeMulti = isMultiComparator(comparator);
  if (draft.comparator && wasMulti === willBeMulti) {
    return { ...draft, comparator };
  }
  return {
    ...draft,
    comparator,
    value: reshapeValueForComparator(draft.value, willBeMulti),
  };
};

export const applyJourneyDraftValue = (
  draft: JourneyConditionDraft,
  next: FormFieldValue
): JourneyConditionDraft => {
  if (!draft.comparator || !isMultiComparator(draft.comparator) || typeof next !== 'string') {
    return { ...draft, value: next };
  }
  return { ...draft, value: toggleMultiValue(draft.value, next) };
};

export const clearJourneyDraftComparator = (
  draft: JourneyConditionDraft
): JourneyConditionDraft => ({
  ...draft,
  comparator: undefined,
  value: undefined,
});

type SaveableJourneyDraft = JourneyConditionDraft & {
  comparator: ConditionComparator;
  value: FormFieldValue;
};

export const isJourneyDraftSaveable = (
  draft: JourneyConditionDraft
): draft is SaveableJourneyDraft =>
  canSaveCondition({
    source: 'journey',
    id: 'draft',
    variable: draft.variable,
    comparator: draft.comparator,
    value: draft.value,
  });

export const journeyDraftToCondition = (
  draft: JourneyConditionDraft,
  id: string
): FormFieldConditionJourney => {
  if (!isJourneyDraftSaveable(draft)) {
    throw new Error('journeyDraftToCondition called on an incomplete draft');
  }
  return {
    source: 'journey',
    id,
    variable: draft.variable,
    comparator: draft.comparator,
    value: draft.value,
  };
};
